/*
 * SK Motion — smooth scroll (Lenis) + scroll animations (GSAP ScrollTrigger).
 * Driven by data attributes so sections stay declarative:
 *   data-sk-split="chars|words"   masked text reveal
 *   data-sk-words                 words light up while scrolling (scrub)
 *   data-sk-reveal                fade + rise on enter
 *   data-sk-stagger               children reveal one after another
 *   data-sk-clip                  image curtain reveal
 *   data-sk-parallax="20"         vertical parallax (yPercent amplitude)
 *   data-sk-marquee="30"          infinite marquee reacting to scroll velocity
 *   data-sk-hscroll               pinned horizontal gallery (desktop)
 *   data-sk-expand                image grows to full screen while pinned
 *   data-sk-hero                  intro timeline of the hero
 *   data-sk-magnetic              element slightly follows the pointer
 *   data-sk-cursor="Voir"         cursor label on hover
 */
(function () {
  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  // Not gated on prefers-reduced-motion: Windows' "animations off" setting (common on office
  // PCs) turns it on, and the site is built on scroll-driven motion the visitor controls.
  if (!window.gsap || !window.ScrollTrigger) {
    root.classList.remove('sk-js');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);
  // Mobile: the address bar resizing the viewport must not re-layout every trigger.
  ScrollTrigger.config({ ignoreMobileResize: true });
  window.skMotionReady = true;

  // Phones scroll fast: everything plays quicker and starts earlier.
  var touch = !finePointer;
  var SPEED = touch ? 0.55 : 1;
  var START = touch ? 'top 94%' : 'top 88%';

  var EASE = 'expo.out';
  var lenis = null;

  /* ---------- Text splitting (keeps inline markup such as <em>) ---------- */
  function split(el, mode) {
    if (el._skParts) return el._skParts;
    var parts = [];

    function makeWord(token) {
      var w = document.createElement('span');
      w.className = mode === 'plain' ? 'sk-w sk-w--plain' : 'sk-w';
      if (mode === 'chars') {
        Array.from(token).forEach(function (c) {
          var inner = document.createElement('span');
          inner.className = 'sk-ci';
          inner.textContent = c;
          w.appendChild(inner);
          parts.push(inner);
        });
      } else {
        var inner = document.createElement('span');
        inner.className = 'sk-wi';
        inner.textContent = token;
        w.appendChild(inner);
        parts.push(inner);
      }
      return w;
    }

    function walk(node) {
      Array.from(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(function (tok) {
            if (!tok) return;
            if (/^\s+$/.test(tok)) frag.appendChild(document.createTextNode(' '));
            else frag.appendChild(makeWord(tok));
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') {
          walk(n);
        }
      });
    }

    walk(el);
    el._skParts = parts;
    return parts;
  }

  function fresh(scope, selector) {
    var list = Array.from(scope.querySelectorAll(selector));
    if (scope.matches && scope.matches(selector)) list.unshift(scope);
    return list.filter(function (el) {
      if (el.hasAttribute('data-sk-init')) return false;
      el.setAttribute('data-sk-init', '');
      return true;
    });
  }

  // Scroll lock for the intro loader and the mobile menu: Lenis when it runs, a CSS class otherwise.
  function lockScroll(on) {
    if (lenis) { if (on) lenis.stop(); else lenis.start(); return; }
    root.classList.toggle('sk-scroll-lock', on);
  }

  /* ---------- Smooth scroll ---------- */
  function initLenis() {
    // Lenis only smooths the mouse wheel; on touch screens scrolling is native anyway, and Lenis
    // still re-measured the page on every frame and every resize. Phones skip it entirely.
    if (!window.Lenis || touch) return;
    lenis = new Lenis({
      duration: 1.15,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      wheelMultiplier: 0.9,
    });
    window.skLenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (time) { lenis.raf(time * 1000); });
    gsap.ticker.lagSmoothing(0);

    // Let Dawn's drawers, modals and search scroll natively.
    var prevent = '.menu-drawer, cart-drawer, .cart-drawer, details-modal, .search-modal, product-modal, .product-media-modal, .mobile-facets, .predictive-search, .cart-notification';
    document.querySelectorAll(prevent).forEach(function (el) { el.setAttribute('data-lenis-prevent', ''); });

    // Pause while Dawn locks the page (open drawer, modal…).
    new MutationObserver(function () {
      if (/overflow-hidden/.test(document.body.className)) lenis.stop();
      else lenis.start();
    }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  // "Back to top" and in-page anchors: Lenis on desktop, native smooth scroll on touch.
  function initLinks() {
    document.addEventListener('click', function (e) {
      var top = e.target.closest('[data-sk-top]');
      if (top) {
        e.preventDefault();
        if (!lenis || window.matchMedia('(pointer: coarse)').matches) {
          // Touch: Lenis does not drive native scrolling, and the address bar resizing the
          // viewport mid-animation left the page short of the top. Native scroll + a final snap.
          window.scrollTo({ top: 0, behavior: 'smooth' });
          setTimeout(function () { if (window.scrollY > 0) window.scrollTo(0, 0); }, 1400);
        } else {
          lenis.scrollTo(0, { duration: 2 });
        }
        return;
      }
      var a = e.target.closest('a[href^="#"]');
      if (!a || a.classList.contains('skip-to-content-link')) return;
      var id = a.getAttribute('href');
      if (id.length < 2) return;
      var target = document.querySelector(id);
      if (target) {
        e.preventDefault();
        if (lenis) lenis.scrollTo(target, { offset: -40 });
        else window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 40, behavior: 'smooth' });
      }
    });
  }

  /* ---------- Intro loader (once per session) ---------- */
  // Heavy work (the 3D story) waits for this so the intro animation stays smooth.
  function introDone() {
    if (window.skIntroDone) return;
    window.skIntroDone = true;
    document.dispatchEvent(new Event('sk:intro-done'));
  }

  // Without the loader (every reload in the session), "intro done" is the end of the hero
  // animation, measured by initHero: the 3D story must not boot while it plays.
  var heroEndMs = 0;
  var noLoader = false;

  function runLoader() {
    var el = document.querySelector('[data-sk-loader]');
    if (!el) { noLoader = true; return 0.1; }
    var seen = false;
    try {
      seen = !!sessionStorage.getItem('sk-loaded');
      sessionStorage.setItem('sk-loaded', '1');
    } catch (e) {}
    if (seen || designMode || getComputedStyle(el).display === 'none') {
      el.remove();
      noLoader = true;
      return 0.1;
    }

    var word = el.querySelector('[data-sk-loader-word]');
    var chars = split(word, 'chars');
    var count = el.querySelector('[data-sk-count]');
    var counter = { v: 0 };
    gsap.set(word, { visibility: 'inherit' });
    lockScroll(true);

    var tl = gsap.timeline({
      onComplete: function () {
        el.remove();
        introDone();
        lockScroll(false);
      },
    });
    // Pages with the 3D story boot it while the loader covers the screen (scroll is locked, nothing
    // to stutter): the count holds near the end until the story reports ready, 2.5 s at most.
    var hold = !!document.querySelector('[data-sk-story]');
    window.skLoaderHold = hold;
    function showCount() { count.textContent = String(Math.round(counter.v)).padStart(3, '0'); }
    var bar = el.querySelector('[data-sk-loader-bar]');

    tl.fromTo(chars, { yPercent: 115 }, { yPercent: 0, duration: 1.1, ease: EASE, stagger: 0.045 }, 0)
      .to(counter, { v: hold ? 90 : 100, duration: 1.7, ease: 'power3.inOut', onUpdate: showCount }, 0)
      .to(bar, { scaleX: hold ? 0.9 : 1, duration: 1.7, ease: 'power3.inOut' }, 0);
    if (hold) {
      tl.addPause(1.7, function () {
        var done = false;
        var creep = gsap.to(counter, { v: 99, duration: 2.5, ease: 'power1.out', onUpdate: showCount });
        function go() {
          if (done) return;
          done = true;
          creep.kill();
          window.skLoaderHold = false;
          document.dispatchEvent(new Event('sk:loader-resume'));
          tl.resume();
        }
        if (window.skStoryReady) go();
        else {
          document.addEventListener('sk:story-ready', go, { once: true });
          setTimeout(go, 2500);
        }
      });
      tl.to(counter, { v: 100, duration: 0.3, ease: 'power2.out', onUpdate: showCount }, 1.7)
        .to(bar, { scaleX: 1, duration: 0.3, ease: 'power2.out' }, 1.7);
    }
    tl.to(chars, { yPercent: -115, duration: 0.7, ease: 'expo.in', stagger: 0.02 }, 1.75)
      .to(el, { clipPath: 'inset(0% 0% 100% 0%)', duration: 1.1, ease: 'expo.inOut' }, 2.05);
    if (touch) tl.timeScale(1.6);
    return touch ? 1.55 : 2.45;
  }

  /* ---------- Hero ---------- */
  // The intro runs as Web Animations on translate / scale / opacity, which the browser plays on
  // the compositor: it stays smooth while the page is still running its load-time scripts (the
  // GSAP version stuttered on every reload). Individual translate/scale properties never fight
  // the scroll-out timeline below, which drives `transform`.
  var EXPO_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';
  var EXPO_IN_OUT = 'cubic-bezier(0.87, 0, 0.13, 1)';
  function play(el, frames, delay, duration, easing) {
    if (!el.animate) { Object.assign(el.style, frames[1]); return 0; }
    var a = el.animate(frames, { delay: delay, duration: duration, easing: easing, fill: 'backwards' });
    // Keep the end state as inline style (the CSS start states hide these elements), then drop the animation.
    a.onfinish = function () { try { a.commitStyles(); } catch (e) { Object.assign(el.style, frames[1]); } a.cancel(); };
    return delay + duration;
  }

  function initHero(scope, delay) {
    fresh(scope, '[data-sk-hero]').forEach(function (hero) {
      var lines = hero.querySelectorAll('[data-sk-hero-line]');
      var chars = [];
      lines.forEach(function (l) { chars = chars.concat(split(l, 'chars')); });
      var media = hero.querySelector('[data-sk-hero-media]');
      var img = media ? media.querySelector('img, svg') : null;
      var fades = hero.querySelectorAll('[data-sk-hero-fade]');
      var kanji = hero.querySelector('[data-sk-kanji]');
      var k = touch ? 1000 / 1.5 : 1000; // seconds -> ms; phones play it 1.5x faster

      function start(t0) {
        var end = 0;
        lines.forEach(function (l) { l.style.visibility = 'inherit'; });
        chars.forEach(function (c, i) {
          end = Math.max(end, play(c, [{ translate: '0 120%' }, { translate: '0 0' }], t0 + i * 0.035 * k, 1.7 * k, EXPO_OUT));
        });
        if (media) end = Math.max(end, play(media, [{ clipPath: 'inset(50% 50% 50% 50%)' }, { clipPath: 'inset(0% 0% 0% 0%)' }], t0 + 0.1 * k, 1.7 * k, EXPO_IN_OUT));
        // The clip-path reveal is the one part that may need the main thread: the 3D story boots as
        // soon as it's done instead of waiting for the whole intro (the hoodie came ~2 s late on reload).
        setTimeout(function () { window.skHeroRevealed = true; document.dispatchEvent(new Event('sk:hero-revealed')); }, media ? t0 + 1.8 * k : t0);
        if (img) end = Math.max(end, play(img, [{ scale: '1.6' }, { scale: '1' }], t0 + 0.25 * k, 2.4 * k, EXPO_OUT));
        if (kanji) end = Math.max(end, play(kanji, [{ opacity: 0, translate: '0 20%' }, { opacity: 1, translate: '0 0' }], t0 + 0.6 * k, 2 * k, EXPO_OUT));
        fades.forEach(function (f, i) {
          end = Math.max(end, play(f, [{ opacity: 0, translate: '0 24px' }, { opacity: 1, translate: '0 0' }], t0 + (0.9 + i * 0.12) * k, 1.3 * k, EXPO_OUT));
        });
        heroEndMs = Math.max(heroEndMs, end);
        return end;
      }

      chars.forEach(function (c) { c.style.translate = '0 120%'; });
      if (window.skLoaderHold) {
        // The loader is holding for the 3D story: start where the hero used to start in the
        // loader's timeline (0.75 s after the hold point, loader speed).
        document.addEventListener('sk:loader-resume', function () { start(0.75 * (touch ? 1000 / 1.6 : 1000)); }, { once: true });
      } else {
        start(delay * 1000);
      }

      var out = gsap.timeline({
        scrollTrigger: { trigger: hero, start: 'top top', end: 'bottom top', scrub: true },
      });
      if (lines[0]) out.to(lines[0], { xPercent: -14, ease: 'none' }, 0);
      if (lines[1]) out.to(lines[1], { xPercent: 14, ease: 'none' }, 0);
      if (media) out.to(media, { yPercent: 22, scale: 0.9, ease: 'none' }, 0);
      if (kanji) out.to(kanji, { yPercent: -70, ease: 'none' }, 0);
    });
  }

  /* ---------- Generic scroll effects ---------- */
  function initReveals(scope) {
    fresh(scope, '[data-sk-split]').forEach(function (el) {
      var mode = el.getAttribute('data-sk-split') === 'chars' ? 'chars' : 'words';
      var parts = split(el, mode);
      gsap.set(el, { visibility: 'inherit' });
      gsap.fromTo(parts, { yPercent: 115 }, {
        yPercent: 0,
        duration: 1.4 * SPEED,
        ease: EASE,
        stagger: (mode === 'chars' ? 0.03 : 0.06) * SPEED,
        scrollTrigger: { trigger: el, start: START, once: true },
      });
    });

    fresh(scope, '[data-sk-words]').forEach(function (el) {
      var parts = split(el, 'plain');
      gsap.fromTo(parts, { opacity: 0.13 }, {
        opacity: 1,
        ease: 'none',
        stagger: 0.1,
        scrollTrigger: { trigger: el, start: touch ? 'top 85%' : 'top 78%', end: touch ? 'bottom 75%' : 'bottom 50%', scrub: true },
      });
    });

    fresh(scope, '[data-sk-reveal]').forEach(function (el) {
      gsap.fromTo(el, { opacity: 0, y: 40 }, {
        opacity: 1,
        y: 0,
        duration: 1.3 * SPEED,
        ease: EASE,
        delay: (parseFloat(el.getAttribute('data-sk-delay')) || 0) * SPEED,
        scrollTrigger: { trigger: el, start: START, once: true },
      });
    });

    fresh(scope, '[data-sk-stagger]').forEach(function (el) {
      gsap.fromTo(el.children, { opacity: 0, y: 36 }, {
        opacity: 1,
        y: 0,
        duration: 1.2 * SPEED,
        ease: EASE,
        stagger: 0.09 * SPEED,
        scrollTrigger: { trigger: el, start: START, once: true },
      });
    });

    fresh(scope, '[data-sk-clip]').forEach(function (el) {
      var media = el.querySelector('img, svg, video');
      var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: START, once: true } });
      tl.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.5 * SPEED, ease: 'expo.inOut' });
      if (media) tl.fromTo(media, { scale: 1.4 }, { scale: 1, duration: 2 * SPEED, ease: EASE }, 0.1 * SPEED);
    });

    fresh(scope, '[data-sk-parallax]').forEach(function (el) {
      var amount = parseFloat(el.getAttribute('data-sk-parallax')) || 16;
      gsap.fromTo(el, { yPercent: -amount / 2 }, {
        yPercent: amount / 2,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });

    fresh(scope, '[data-sk-marquee]').forEach(function (el) {
      var track = el.querySelector('[data-sk-marquee-track]');
      if (!track) return;
      var duration = parseFloat(el.getAttribute('data-sk-marquee')) || 30;
      var reverse = el.getAttribute('data-sk-direction') === 'right';
      var loop = gsap.fromTo(track, { xPercent: reverse ? -50 : 0 }, { xPercent: reverse ? 0 : -50, duration: duration, ease: 'none', repeat: -1 });
      var skew = gsap.quickTo(track, 'skewX', { duration: 0.6, ease: 'power3' });
      ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onUpdate: function (self) {
          var boost = gsap.utils.clamp(-5, 5, self.getVelocity() / 350);
          gsap.to(loop, {
            timeScale: self.direction * (1 + Math.abs(boost)),
            duration: 0.25,
            overwrite: true,
            onComplete: function () { gsap.to(loop, { timeScale: self.direction, duration: 1.2 }); },
          });
          skew(-boost * 1.6);
          gsap.delayedCall(0.15, function () { skew(0); });
        },
      });
    });

    fresh(scope, '[data-sk-expand]').forEach(function (el) {
      var section = el.closest('[data-sk-expand-wrap]') || el.parentElement;
      var img = el.querySelector('img, svg');
      var shade = section.querySelector('[data-sk-shade]');
      var tl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=110%', scrub: 1, pin: true, anticipatePin: 1 },
      });
      tl.fromTo(el, { clipPath: 'inset(22% 30% 22% 30%)' }, { clipPath: 'inset(0% 0% 0% 0%)', ease: 'none' }, 0);
      if (img) tl.fromTo(img, { scale: 1.3 }, { scale: 1, ease: 'none' }, 0);
      if (shade) tl.fromTo(shade, { opacity: 0 }, { opacity: 1, ease: 'none' }, 0.35);
    });

    fresh(scope, '[data-sk-magnetic]').forEach(function (el) {
      if (!finePointer) return;
      var xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      var yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.4)' });
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.25);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.35);
      });
      el.addEventListener('pointerleave', function () { xTo(0); yTo(0); });
    });
  }

  /* ---------- Pinned horizontal gallery ---------- */
  function initHScroll(scope) {
    fresh(scope, '[data-sk-hscroll]').forEach(function (section) {
      var mm = gsap.matchMedia();
      // Every screen size: the section pins, the colours slide left, then the page scrolls on.
      mm.add('all', function () {
        var track = section.querySelector('[data-sk-track]');
        var bar = section.querySelector('[data-sk-progress]');
        if (!track) return;
        var distance = function () { return Math.max(0, track.scrollWidth - section.clientWidth); };

        var move = gsap.to(track, {
          x: function () { return -distance(); },
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: function () { return '+=' + distance(); },
            pin: true,
            scrub: 1,
            anticipatePin: 1,
            invalidateOnRefresh: true,
          },
        });
        if (bar) {
          gsap.fromTo(bar, { scaleX: 0 }, {
            scaleX: 1,
            ease: 'none',
            scrollTrigger: { trigger: section, start: 'top top', end: function () { return '+=' + distance(); }, scrub: true },
          });
        }
        section.querySelectorAll('[data-sk-hcard]').forEach(function (card) {
          var img = card.querySelector('img, svg');
          if (!img) return;
          gsap.fromTo(img, { xPercent: -7 }, {
            xPercent: 7,
            ease: 'none',
            scrollTrigger: { trigger: card, containerAnimation: move, start: 'left right', end: 'right left', scrub: true },
          });
        });
      });
    });
  }

  /* ---------- Mobile menu ---------- */
  // Replaces Dawn's drawer below 990px: it hung off the hide-on-scroll header, so it opened
  // off-screen mid-page. This panel is fixed to the viewport and only animates transform/opacity.
  function initMenu() {
    var menu = document.querySelector('[data-sk-menu]');
    if (!menu) return;
    var mq = window.matchMedia('(max-width: 989px)');
    var html = document.documentElement;
    var opener = null;
    function open(btn) {
      opener = btn;
      menu.setAttribute('aria-hidden', 'false');
      html.classList.add('sk-menu-open');
      lockScroll(true);
      var first = menu.querySelector('.sk-menu__nav a');
      setTimeout(function () { if (first) first.focus({ preventScroll: true }); }, 300);
    }
    function close() {
      if (!html.classList.contains('sk-menu-open')) return;
      menu.setAttribute('aria-hidden', 'true');
      html.classList.remove('sk-menu-open');
      lockScroll(false);
      if (opener) opener.focus({ preventScroll: true });
    }
    // Capture phase: runs before Dawn's <details> toggles its own drawer.
    document.addEventListener('click', function (e) {
      var burger = e.target.closest('header-drawer summary, .header__icon--menu');
      if (burger && mq.matches) {
        e.preventDefault();
        e.stopPropagation();
        open(burger);
        return;
      }
      if (e.target.closest('[data-sk-menu-close]') || e.target.closest('.sk-menu__nav a')) close();
    }, true);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    mq.addEventListener('change', function () { if (!mq.matches) close(); });
  }

  /* ---------- Cursor & page progress ---------- */
  function initCursor() {
    if (!finePointer) return;
    var c = document.createElement('div');
    c.className = 'sk-cursor';
    c.setAttribute('aria-hidden', 'true');
    c.innerHTML = '<span class="sk-cursor__dot"></span><span class="sk-cursor__label"></span>';
    document.body.appendChild(c);
    var label = c.querySelector('.sk-cursor__label');
    var xTo = gsap.quickTo(c, 'x', { duration: 0.45, ease: 'power3' });
    var yTo = gsap.quickTo(c, 'y', { duration: 0.45, ease: 'power3' });

    window.addEventListener('pointermove', function (e) {
      xTo(e.clientX);
      yTo(e.clientY);
      c.classList.add('is-visible');
    }, { passive: true });
    document.documentElement.addEventListener('pointerleave', function () { c.classList.remove('is-visible'); });
    document.addEventListener('pointerover', function (e) {
      var target = e.target.closest ? e.target.closest('[data-sk-cursor], a, button, summary, label, select') : null;
      var text = target && target.getAttribute('data-sk-cursor');
      c.classList.toggle('is-hover', !!target);
      c.classList.toggle('has-label', !!text);
      label.textContent = text || '';
    });
  }

  function initProgress() {
    var bar = document.querySelector('[data-sk-page-progress]');
    if (!bar) return;
    gsap.fromTo(bar, { scaleX: 0 }, {
      scaleX: 1,
      ease: 'none',
      scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 0.3 },
    });
  }

  /* ---------- Boot ---------- */
  function init(scope, heroDelay) {
    initHero(scope, heroDelay);
    initReveals(scope);
    initHScroll(scope);
  }

  initLenis();
  initLinks();
  initMenu();
  // A reload in the middle of the 3D story landed there while it was still booting: the story
  // page always reopens at the top, where the intro plays.
  if (document.querySelector('[data-sk-story]') && 'scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
    // Again on leave: a replaceState (Shopify strips URL params) resets it to "auto" for the entry.
    window.addEventListener('pagehide', function () { history.scrollRestoration = 'manual'; });
    window.scrollTo(0, 0);
  }

  var heroDelay = runLoader();
  initCursor();
  initProgress();
  init(document, heroDelay);
  if (noLoader) setTimeout(introDone, heroEndMs);

  // ScrollTrigger already refreshes itself on window load; a second full refresh there (every pin
  // and trigger re-measured) landed mid hero-intro. Only fonts arriving after load need one.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { if (document.readyState === 'complete') ScrollTrigger.refresh(); });

  // Theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target, 0);
    ScrollTrigger.refresh();
  });
  document.addEventListener('shopify:section:unload', function () {
    setTimeout(function () {
      ScrollTrigger.getAll().forEach(function (t) {
        if (t.trigger && !document.contains(t.trigger)) t.kill();
      });
      ScrollTrigger.refresh();
    }, 50);
  });
})();
